import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { batch } from 'react-redux';
import { RootState } from '..';
import { queryClient } from '../../api/APIProvider';
import { getCompanies } from '../../api/hooks/useCompanies';
import { Company, ProviderSettings } from '../../interfaces/company.interface';
import { SetBooleanPayload } from '../../interfaces/store.interface';
import { getSelectedCompanyStorage, setSelectedCompanyStorage } from '../../services/localStorage.service';
import { setServerError } from '../core/coreSlice';
import { selectUser } from '../user/userSlice';
import { CompaniesSlice, CompaniesState, SetCompaniesPayload, SetCompanyPayload } from './companiesSlice.interface';

const initialState: CompaniesState = {
  loading: false,
  selectedCompany: null,
  companies: [],
};

export const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    setCompaniesLoading: (state: CompaniesState, action: PayloadAction<SetBooleanPayload>): void => {
      state.loading = action.payload.value;
    },
    setSelectedCompany: (state: CompaniesState, action: PayloadAction<SetCompanyPayload>): void => {
      state.selectedCompany = action.payload.company;
    },
    setCompanies: (state: CompaniesState, action: PayloadAction<SetCompaniesPayload>): void => {
      state.companies = action.payload.companies;
    },
    resetCompanies: (): CompaniesState => initialState,
  },
});

//////////////////////////////// ACTIONS ////////////////////////////////
export const { setCompaniesLoading, setSelectedCompany, setCompanies, resetCompanies } = companiesSlice.actions;

//////////////////////////////// SELECTORS ////////////////////////////////
export const selectCompaniesLoading = (state: CompaniesSlice): boolean => state.companies.loading;
export const selectSelectedCompany = (state: CompaniesSlice): Company | null => state.companies.selectedCompany;
export const selectCompanyProviderSettings = (state: CompaniesSlice): ProviderSettings | null | undefined =>
  state?.companies?.selectedCompany?.settings?.provider;

export const selectSelectedCompanyName = (state: CompaniesSlice): string => {
  const branchSerial = state.companies.selectedCompany?.branchSerial || 0;
  if (branchSerial > 0) {
    return `${state.companies.selectedCompany?.title} (${branchSerial})`;
  }

  return state.companies.selectedCompany?.title || '';
};

export const selectShippingOfCompany = createSelector(selectSelectedCompany, (state) => state?.shipping || []);

export const hasCompanyProviderEnabled = createSelector(
  selectCompanyProviderSettings,
  (provider) => provider?.invoice?.enabled || provider?.receipt?.enabled,
);

export default companiesSlice.reducer;

export const getCompaniesAsync = createAsyncThunk('companies/getCompanies', async (_, { dispatch, getState }) => {
  // Access the state
  const state: RootState = getState() as RootState;
  const user = state?.user?.user;
  if (!user) return;

  batch(() => {
    dispatch(setCompaniesLoading({ value: true }));
    dispatch(setServerError({ value: null }));
  });

  const companies = await getCompanies();
  if (companies.thrownError) {
    dispatch(setServerError({ value: i18next.t('API_ERRORS.SERVER_IS_DOWN') }));
    return;
  }

  const selectedCompany = await getSelectedCompanyStorage();

  const isCompanyValid = companies.find((comp) => comp.id === selectedCompany?.id);

  if (isCompanyValid) {
    dispatch(setSelectedCompany({ company: isCompanyValid }));
  } else {
    if (companies[0]) {
      dispatch(setSelectedCompany({ company: companies[0] }));
      await setSelectedCompanyStorage(companies[0]);
    }

    // TODO: what should happen if no companies are available
  }

  dispatch(setCompanies({ companies }));
  dispatch(setCompaniesLoading({ value: false }));
});

export const setSelectedCompanyAsync = createAsyncThunk(
  'companies/setSelectedCompany',
  async (data: Company | null, { dispatch }) => {
    dispatch(setSelectedCompany({ company: data }));

    await setSelectedCompanyStorage(data);
    await queryClient.invalidateQueries();
  },
);

export const selectIsCompanyOwner = createSelector(selectUser, selectSelectedCompany, (user, company) => {
  return company?.ownerId === user?.user?.id;
});
